import db from "../db/database";
import {
  fetchFromBusinessCentral,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { getPendingSync, isOnline } from "./syncService";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import { anotarError, anotarFila, anotarInicioDeLote, anotarResumenDelLote } from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "turnos";

// Función para transformar un turno al formato esperado por Business Central
// Un turno en el formato que espera BC.
const turnoParaBC = (turno) => ({
  No: turno.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
  DescripcionTurno: turno.DescripcionTurno, // Enviamos DescripcionTurno como Turno a BC
  HorarioInicio: turno.HorarioInicio,
  HorarioFin: turno.HorarioFin || "",
  Deleted: turno.deleted,
});

// Payload de la acción, con todos los turnos que se manden en la misma llamada.
//
// Importaciones_insertaTurnos siempre recibió un array y lo recorre con un
// foreach; el TPV era el que mandaba uno solo por viaje. Cada turno se procesa
// aislado del lado de BC (TryProcesarTurno), así que un rechazo no arrastra a
// los demás y la respuesta trae un resultado por registro, en el mismo orden.
const transformTurnosForBC = (turnos) => ({
  data: JSON.stringify({ Turnos: turnos.map(turnoParaBC) }),
});

const transformTurnoForBC = (turno) => transformTurnosForBC([turno]);

// BC rechazó la baja: se revierte el borrado local para que el turno vuelva a
// aparecer en el listado y local y BC no queden divergentes. Mismo criterio que
// en userSyncService.
const restoreDeletedTurno = async (turno, reason) => {
  await db.turnos.update(turno.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Sincroniza un solo turno contra BC, para el botón de sincronizar de cada fila
// del listado. Devuelve { ok, message } para poder mostrarle el resultado a
// quien apretó el botón, en vez de acumularlo en un resumen.
export const syncTurnoById = async (turnoId) => {
  if (!isOnline()) {
    return { ok: false, message: "No hay conexión a internet" };
  }

  try {
    const turno = await db.turnos.get(turnoId);
    if (!turno) {
      return { ok: false, message: "Turno no encontrado" };
    }

    if (turno.deleted) {
      // Nunca llegó a BC: sólo existe en local.
      if (!turno.No) {
        await db.turnos.delete(turno.id);
        return { ok: true, message: "Turno eliminado" };
      }

      const response = await sendToBusinessCentral(
        "Importaciones_insertaTurnos",
        transformTurnoForBC(turno),
        true
      );
      const result = parseImportResponse(response, turno.No);

      if (!result.ok) {
        await restoreDeletedTurno(turno, result.message);
        return {
          ok: false,
          message:
            result.message || "Business Central no aplicó la baja. El turno sigue en el listado.",
        };
      }

      await db.turnos.delete(turno.id);
      return { ok: true, message: "Turno dado de baja en Business Central" };
    }

    const response = await sendToBusinessCentral(
      "Importaciones_insertaTurnos",
      transformTurnoForBC(turno),
      true
    );
    const result = parseImportResponse(response, turno.No || "TEMP");

    if (!result.ok || !result.assignedNo) {
      return {
        ok: false,
        message: result.message || "Respuesta inválida de Business Central",
      };
    }

    // Se lee antes del update, que es justo lo que lo borra.
    const teniaBajaRechazada = Boolean(turno.syncError);

    await db.turnos.update(turno.id, {
      No: result.assignedNo,
      lastModified: new Date(),
      lastSynced: new Date(),
      pendingSync: 0,
      syncError: null,
    });

    return {
      ok: true,
      message: conAvisoDeBajaRechazada("Turno sincronizado correctamente.", teniaBajaRechazada),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${turnoId}`, error);
    return {
      ok: false,
      message: formatAxiosError(error, "No se pudo sincronizar el turno"),
    };
  }
};

export const syncTurnos = async () => {
  if (!isOnline()) {
    return false;
  }

  // Turnos cuyo push a BC falló en esta corrida (ej. falta la Serie de
  // Numeración de "Turno" en Conf. ventas y cobros de Business Central).
  // Cada entrada es { turno, action: "delete" | "save", reason }.
  const failedTurnos = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos: sin eso, una
  // sincronización de veinte turnos con dos rechazos no deja ver qué pasó con
  // los otros dieciocho.
  const detalle = [];

  try {
    const pendingTurnos = await getPendingSync("turnos");

    if (pendingTurnos.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingTurnos);

      for (const turno of soloLocales) {
        await db.turnos.delete(turno.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: turno.DescripcionTurno || "(sin descripción)",
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminado sólo en local: nunca llegó a Business Central",
            },
            turno.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        anotarInicioDeLote(ENTIDAD, aEnviar.length);

        // UNA llamada para todos los pendientes, en vez de una por turno. Con 20
        // turnos eran 20 viajes de ida y vuelta; ahora es uno. BC procesa cada
        // registro aislado y devuelve un resultado por cada uno, en orden.
        const lote = await enviarLote(
          "Importaciones_insertaTurnos",
          transformTurnosForBC(aEnviar),
          aEnviar.length,
          "turnos"
        );

        // El cruce es por posición: BC devuelve los resultados en el mismo orden
        // en que recibió los turnos. Por No_ no se podría, porque todas las altas
        // se mandan como "TEMP" y volverían indistinguibles entre sí.
        for (let indice = 0; indice < aEnviar.length; indice++) {
          const turno = aEnviar[indice];
          const resultado = lote.results[indice];
          let traiaBajaRechazada = false;

          if (turno.deleted) {
            if (resultado.ok) {
              await db.turnos.delete(turno.id);
            } else {
              // BC no aplicó la baja: el turno NO se borra en local, se revierte
              // el borrado y el motivo queda en el registro.
              await restoreDeletedTurno(turno, resultado.message);
              failedTurnos.push({ turno, action: "delete", reason: resultado.message });
            }
          } else if (resultado.ok && resultado.assignedNo) {
            // Se anota antes del update, que es lo que borra el motivo anterior.
            traiaBajaRechazada = Boolean(turno.syncError);

            await db.turnos.update(turno.id, {
              No: resultado.assignedNo,
              lastModified: new Date(),
              lastSynced: new Date(),
              pendingSync: 0,
              syncError: null,
            });
          } else {
            const motivo = motivoDeGuardadoFallido(resultado);
            failedTurnos.push({ turno, action: "save", reason: motivo });
          }

          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: turno.DescripcionTurno || "(sin descripción)",
                no: resultado.assignedNo || turno.No || null,
                accion: resultado.action || (turno.deleted ? "deleted" : "saved"),
                // Un alta correcta sin número asignado no cuenta como correcta: el
                // turno se queda pendiente y el cuadro tiene que decirlo.
                ok: turno.deleted ? resultado.ok : resultado.ok && Boolean(resultado.assignedNo),
                mensaje: conAvisoDeBajaRechazada(
                  resultado.message || "",
                  traiaBajaRechazada
                ).trim(),
              },
              turno.id,
              turno.deleted ? "baja" : undefined
            )
          );
        }
      }
    }

    // Obtener turnos del servidor después de sincronizar los pendientes
    const serverTurnos = await fetchFromBusinessCentral("Turnos");
    const localTurnos = await db.turnos.toArray();

    // Mapa para buscar turnos locales por descripción rápidamente (ya que BC usa "Turno" como nombre del campo)
    const localTurnosByDesc = {};
    localTurnos.forEach((turno) => {
      if (turno.DescripcionTurno) {
        localTurnosByDesc[turno.DescripcionTurno] = turno;
      }
    });

    // Actualizar turnos existentes y añadir nuevos desde el servidor
    for (const serverTurno of serverTurnos) {
      // Verificar si el turno ya existe localmente por descripción
      const localTurno = localTurnosByDesc[serverTurno.Turno];

      if (localTurno) {
        // Actualizar turno existente si no está pendiente de sincronización
        if (localTurno.pendingSync === 0) {
          await db.turnos.update(localTurno.id, {
            HorarioInicio: serverTurno.HorarioInicio,
            HorarioFin: serverTurno.HorarioFin,
            No: serverTurno.No, // Actualizar el No asignado por BC
            lastModified: new Date(),
            lastSynced: new Date(),
          });
        }
      } else {
        // Añadir nuevo turno del servidor si no existe localmente
        await db.turnos.add({
          DescripcionTurno: serverTurno.Turno, // Guardamos el Turno del servidor como DescripcionTurno
          No: serverTurno.No, // Guardar el No asignado por BC
          HorarioInicio: serverTurno.HorarioInicio,
          HorarioFin: serverTurno.HorarioFin,
          lastSynced: new Date(),
          pendingSync: 0,
          syncError: null,
          deleted: false,
        });
      }
    }

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedTurnos.length > 0) {
      const deletes = failedTurnos.filter((f) => f.action === "delete");
      const saves = failedTurnos.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} turno(s) no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(`${saves.length} turno(s) no se pudieron guardar en Business Central`);
      }

      // Motivo concreto que devolvió BC, si lo hubo.
      const reason = failedTurnos.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedTurnos.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos, y `executeWithRetry` sólo mira
    // `success === false`, así que un objeto acá no cambia nada para syncAll.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};
